'use client';

import { Box, Divider, Stack, Typography } from '@mui/material';
import { IngestionSourceType } from '@repo/api-gw-sdk';

import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';

import type { IngestionPipelineWizardState } from '../IngestionPipelineWizardState';
import { getStepperLabels } from '../kafkaHelpers';
import { PathBuilder } from '../partitioning/PathBuilder';

const Field = ({ label, value }: { label: string; value: string }) => (
  <Stack direction='row' gap='16px'>
    <Typography
      variant='body2'
      sx={{
        color: 'text.secondary',
        minWidth: '160px',
      }}
    >
      {label}
    </Typography>
    <Typography variant='body2'>{value}</Typography>
  </Stack>
);

export function ReviewStep(props: StepProps<IngestionPipelineWizardState>) {
  const { wizardState } = props;
  const isKafka =
    wizardState.sourceType === IngestionSourceType.INGESTION_SOURCE_TYPE_KAFKA;

  const getSourceTypeDisplayText = () => {
    if (
      wizardState.sourceType === IngestionSourceType.INGESTION_SOURCE_TYPE_S3
    ) {
      return 'S3';
    }
    if (
      wizardState.sourceType === IngestionSourceType.INGESTION_SOURCE_TYPE_GCS
    ) {
      return 'GCS';
    }
    if (isKafka) {
      return 'Kafka';
    }
    return wizardState.sourceType;
  };

  const labels = getStepperLabels(wizardState.sourceType);

  return (
    <StepContainer
      stepperIndex={labels.length - 1}
      stepperLabels={labels}
      canGoNext={() => true}
      onNextClick={async () => {
        await props.finish(
          wizardState.isUpdateMode
            ? 'Ingestion pipeline updated successfully'
            : 'Ingestion pipeline created successfully'
        );
      }}
      onBackClick={() => props.back()}
      nextButtonText={wizardState.isUpdateMode ? 'Update' : 'Create'}
      sx={{ overflow: 'auto', paddingX: '0px', paddingY: 'min(60px, 4vw)' }}
    >
      <Stack gap='min(60px, 4vw)' divider={<Divider />}>
        {/* Pipeline Details Section */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Pipeline Details</Typography>
              <Typography variant='body2' color='text.secondary'>
                Review the pipeline name and target table.
              </Typography>
            </Stack>
            <Stack gap='16px'>
              <Field label='Pipeline Name' value={wizardState.name} />
              <Field label='Table Namespace' value={wizardState.namespace} />
              <Field label='Table Name' value={wizardState.tableName} />
            </Stack>
          </Stack>
        </Box>

        {/* Source Section */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Source</Typography>
              <Typography variant='body2' color='text.secondary'>
                Review the data source configuration.
              </Typography>
            </Stack>
            <Stack gap='16px'>
              <Field label='Source Type' value={getSourceTypeDisplayText()} />

              {wizardState.sourceType ===
              IngestionSourceType.INGESTION_SOURCE_TYPE_S3 ? (
                <>
                  <Field label='Bucket' value={wizardState.s3Bucket} />
                  <Field label='Region' value={wizardState.s3Region} />
                  <Field
                    label='Owner Account ID'
                    value={wizardState.s3OwnerAccountId}
                  />
                  <Field label='File Format' value={wizardState.fileFormat} />
                </>
              ) : isKafka ? (
                <>
                  <Field
                    label='Bootstrap Servers'
                    value={wizardState.kafkaBootstrapServers}
                  />
                  <Field label='Topic' value={wizardState.kafkaTopic} />
                  {wizardState.kafkaConsumerGroupId && (
                    <Field
                      label='Consumer Group'
                      value={wizardState.kafkaConsumerGroupId}
                    />
                  )}
                  <Field
                    label='Starting Offset'
                    value={wizardState.kafkaStartingOffset}
                  />
                  <Field
                    label='Message Format'
                    value={wizardState.kafkaMessageFormat}
                  />
                  <Field
                    label='Key Handling'
                    value={wizardState.kafkaKeyHandling}
                  />
                </>
              ) : (
                <>
                  <Field label='Bucket' value={wizardState.gcsBucket} />
                  <Field label='Location' value={wizardState.gcsLocation} />
                  <Field label='Project ID' value={wizardState.gcsProjectId} />
                  <Field label='File Format' value={wizardState.fileFormat} />
                </>
              )}
            </Stack>
          </Stack>
        </Box>

        {/* Kafka Security Section */}
        {isKafka && (
          <Box paddingX='min(100px, 6vw)'>
            <Stack gap='24px' maxWidth='540px'>
              <Stack gap='8px'>
                <Typography variant='h6'>Security</Typography>
                <Typography variant='body2' color='text.secondary'>
                  Review the Kafka security configuration.
                </Typography>
              </Stack>
              <Stack gap='16px'>
                <Field
                  label='Protocol'
                  value={wizardState.kafkaSecurityProtocol}
                />
                {wizardState.kafkaSaslMechanism && (
                  <Field
                    label='SASL Mechanism'
                    value={wizardState.kafkaSaslMechanism}
                  />
                )}
                {wizardState.kafkaCredentialsSecretArn && (
                  <Field
                    label='Credentials ARN'
                    value={wizardState.kafkaCredentialsSecretArn}
                  />
                )}
                {wizardState.kafkaTlsSecretArn && (
                  <Field
                    label='TLS ARN'
                    value={wizardState.kafkaTlsSecretArn}
                  />
                )}
                <Field
                  label='Skip TLS Verify'
                  value={wizardState.kafkaSkipTlsVerification ? 'Yes' : 'No'}
                />
              </Stack>
            </Stack>
          </Box>
        )}

        {/* Kafka Schema Registry Section */}
        {isKafka && wizardState.kafkaSchemaRegistryEnabled && (
          <Box paddingX='min(100px, 6vw)'>
            <Stack gap='24px' maxWidth='540px'>
              <Stack gap='8px'>
                <Typography variant='h6'>Schema Registry</Typography>
                <Typography variant='body2' color='text.secondary'>
                  Review the schema registry settings.
                </Typography>
              </Stack>
              <Stack gap='16px'>
                <Field label='URL' value={wizardState.kafkaSchemaRegistryUrl} />
                <Field
                  label='Auth Type'
                  value={wizardState.kafkaSchemaRegistryAuthType}
                />
                {wizardState.kafkaSubjectNamingStrategy && (
                  <Field
                    label='Subject Strategy'
                    value={wizardState.kafkaSubjectNamingStrategy}
                  />
                )}
                <Field
                  label='Evolution Mode'
                  value={wizardState.kafkaSchemaEvolutionMode}
                />
              </Stack>
            </Stack>
          </Box>
        )}

        {/* Kafka Settings Section */}
        {isKafka && (
          <Box paddingX='min(100px, 6vw)'>
            <Stack gap='24px' maxWidth='540px'>
              <Stack gap='8px'>
                <Typography variant='h6'>Settings</Typography>
                <Typography variant='body2' color='text.secondary'>
                  Review partitioning, batch, and error handling settings.
                </Typography>
              </Stack>
              <Stack gap='16px'>
                <Field
                  label='Partitioning'
                  value={wizardState.kafkaPartitioningColumns
                    .filter((c) => c.trim() !== '')
                    .join(', ')}
                />
                <Field label='Batch Mode' value={wizardState.kafkaBatchMode} />
                {wizardState.kafkaMaxRecordsPerBatch && (
                  <Field
                    label='Max Records/Batch'
                    value={wizardState.kafkaMaxRecordsPerBatch}
                  />
                )}
                {wizardState.kafkaMaxBatchIntervalSeconds && (
                  <Field
                    label='Max Interval (s)'
                    value={wizardState.kafkaMaxBatchIntervalSeconds}
                  />
                )}
                <Field
                  label='Error Policy'
                  value={wizardState.kafkaErrorPolicy}
                />
                <Field
                  label='Max Retries'
                  value={wizardState.kafkaMaxRetries}
                />
              </Stack>
            </Stack>
          </Box>
        )}

        {/* Partitioning Section (S3/GCS only) */}
        {!isKafka && (
          <Box paddingX='min(100px, 6vw)'>
            <Stack gap='24px' maxWidth='540px'>
              <Stack gap='8px'>
                <Typography variant='h6'>Partitioning</Typography>
                <Typography variant='body2' color='text.secondary'>
                  Review the partition path configuration.
                </Typography>
              </Stack>
              <Stack gap='16px'>
                <Field
                  label='Separator'
                  value={wizardState.partitionSpec.separator}
                />
                <PathBuilder
                  segments={wizardState.partitionSpec.segments}
                  separator={wizardState.partitionSpec.separator}
                  readOnly
                />
              </Stack>
            </Stack>
          </Box>
        )}
      </Stack>
    </StepContainer>
  );
}
